import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Promise from 'bluebird';
import { contains, noop } from 'underscore';

import { DefaultButton } from '@fluentui/react/lib/Button';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useDefaultEditorStyle } from '@bingads-webui-campaign-react/smart-component-util';

const ImageConstraints = {
  minWidth: 40,
  minHeight: 40,
  allowedFileExtensions: [
    '.png',
    '.jpg',
    '.jpeg',
  ],
  maxSize: 30 * 1024 * 1024,
};

function getFileExtension(name) {
  const matches = name.match(/.*(\.[^.]+)$/);

  return matches && matches[1] && matches[1].toLowerCase();
}

const validateImageDimensionsAsync = imageData => new Promise((resolve, reject) => {
  const img = new Image();

  img.onload = () => {
    if (img.naturalWidth < ImageConstraints.minWidth ||
      img.naturalHeight < ImageConstraints.minHeight) {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject({
        minWidth: ImageConstraints.minWidth,
        minHeight: ImageConstraints.minHeight,
      });
    } else {
      resolve(imageData);
    }
  };

  img.src = imageData;
});

const getStyles = (_, { palette = {} }) => ({
  input: {},
  container: {
    margin: '16px 0 0 25px',
    '& $input': {
      display: 'none',
    },
  },
  uploadBtnContainer: {
    display: 'flex',
    alignItems: 'center',
    '& .ms-Button-label': {
      whiteSpace: 'nowrap',
    },
  },
  fileName: {
    marginLeft: 8,
    width: '70%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  icon: {
    color: palette.themePrimary,
  },
  formatMsg: {
    marginTop: 8,
    color: palette.neutralSecondary,
    fontSize: 12,
  },
  error: {
    color: '#a80000',
  },
});

const UnStyledImageUploadLocal = ({
  i18n,
  onFileSelected,
  classes,
  className,
  btnClassName,
  label,
  hidden,
  defaultFileName,
  itemToUnselect,
  resetItemToUnselect,
}) => {
  const fileInput = useRef(null);
  const [fileName, setFileName] = useState(defaultFileName);
  const [errorMsg, setErrorMsg] = useState('');
  const {
    sectionTitleClassName,
  } = useDefaultEditorStyle();

  const onButtonClicked = () => {
    fileInput.current.click();
  };
  const handleFileChange = (event) => {
    const { target: { files: [file] } } = event;

    if (!file) {
      setFileName(null);
      setErrorMsg(null);
      onFileSelected(null);
    } else {
      const { name, size } = file;

      setFileName(name);
      if (!contains(ImageConstraints.allowedFileExtensions, getFileExtension(name))) {
        setErrorMsg(i18n.getString(_TL_('Please upload a .jpg, .jpeg, or .png image.')));
        onFileSelected(null);
      } else if (size > ImageConstraints.maxSize) {
        setErrorMsg(i18n.getString(_TL_('The image you\'ve uploaded is too large. Make sure the size of the uploaded image does not exceed {{maxSize}}MB and try again.'), {
          maxSize: ImageConstraints.maxSize / 1024 / 1024,
        }));
        onFileSelected(null);
      } else {
        const reader = new FileReader();

        reader.onload = (e) => {
          const imageData = e.target.result;

          return validateImageDimensionsAsync(imageData)
            .then(() => {
              setErrorMsg(null);
              onFileSelected({ imageData, file });
            })
            .catch((err) => {
              setErrorMsg(i18n.getString(_TL_('The image you\'ve uploaded is too small. Make sure images are at least {{width}} x {{height}} pixels and try again.'), {
                width: err.minWidth,
                height: err.minHeight,
              }));
              onFileSelected(null);
            });
        };

        reader.readAsDataURL(file);
      }
    }
  };

  useEffect(() => {
    if (itemToUnselect && itemToUnselect.file) {
      setFileName(null);
      setErrorMsg(null);
      resetItemToUnselect(itemToUnselect);
    }
  }, [itemToUnselect, resetItemToUnselect]);

  return hidden ? null : (
    <div className={classnames('upload-local-section', className)}>
      {label && <div className={sectionTitleClassName}>{label}</div>}
      <div className={classnames(classes.container, btnClassName)}>
        <div className={classes.uploadBtnContainer}>
          <DefaultButton
            ariaLabel={label}
            iconProps={{ iconName: 'Upload', className: classes.icon }}
            onClick={onButtonClicked}
          >
            {i18n.getString(_TL_('Upload'))}
          </DefaultButton>
          <div
            className={classnames(classes.fileName, 'smart-page-uploaded-file-name')}
            title={fileName}
          >
            {fileName}
          </div>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept=".png,.jpg,.jpeg"
          onChange={handleFileChange}
          className={classes.input}
        />
        <div className={classnames(classes.error, 'smart-page-upload-error')}>{errorMsg}</div>
        <div className={classes.formatMsg}>
          {i18n.getString(_TL_('Supported formats: .jpg, .jpeg, or .png'))}
        </div>
      </div>
    </div>
  );
};

UnStyledImageUploadLocal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string,
  btnClassName: PropTypes.string,
  onFileSelected: PropTypes.func.isRequired,
  label: PropTypes.string,
  hidden: PropTypes.bool,
  defaultFileName: PropTypes.string,
  itemToUnselect: PropTypes.shape({}),
  resetItemToUnselect: PropTypes.func,
};

UnStyledImageUploadLocal.defaultProps = {
  className: undefined,
  btnClassName: undefined,
  label: undefined,
  hidden: false,
  defaultFileName: null,
  itemToUnselect: null,
  resetItemToUnselect: noop,
};

export const ImageUploadLocal = withDefaultStyles(UnStyledImageUploadLocal, getStyles);
